import { useEffect, useMemo, useRef } from 'react';
import { createEventService, useStompService, resolvePlugin, resolveCaptionAction } from '@bb/event-service';
import { sendPatchesFromDrivePreset, removeBaseDataPreset } from '@aelive/dds-client';
import { getStoredSession } from '../utils/sessionManager';
import { dds } from '../dds/ddsClient';
import { upsertTeamStat } from '../utils/liveEventUtils';
import { logCommand, updateCommandLog } from '../utils/commandLog';
import { logClientSessionEvent } from '../services/bbSession.service';
import { useSceneDefinitions } from './useSceneDefinitions';
import { isCopilotView } from '../utils/viewMode';

// Same mutation sendLiveEventPreset emits for the `event` collection. Used
// directly on both the create and update paths so team-less builder events
// (clock / additional_time) can persist — sendLiveEventPreset's client-side
// validation requires team_id, which those events deliberately omit. Goals/
// cards still send team_id in eventData and write identically either way.
const SEND_LIVE_EVENT_MUTATION = `
  mutation SendLiveEvent($collection: String!, $key: JSON!, $data: JSON!, $originSite: String, $op: String) {
    sendLiveEventToDWS(collection: $collection, key: $key, data: $data, originSite: $originSite, op: $op)
  }
`;

/**
 * Creates and returns a wired-up eventService instance for AE Drive.
 *
 * Extracts the inline driver wiring from TXLive.jsx into a reusable hook,
 * matching the same pattern used in Aether's useEventService.
 *
 * @param {object} params
 * @param {boolean} [params.isLive]                - gates STOMP publish
 * @param {boolean}  [params.isNleEnabled]        - gates the /topic/nle publish
 * @param {object}   [params.renderer]            - selectedRenderer; NLE caption URLs are built from its scenes
 * @param {string}   [params.fixtureName]         - the fixture's display name, sent as fixture_name with every event
 * @param {function} [params.onFixtureStateUpdate] - optimistic local state updater
 * @param {function} [params.onRendererDispatch]   - renderer.dispatch handler
 */
export function useEventService({
  isLive = false,
  isNleEnabled = false,
  renderer,
  fixtureName,
  onFixtureStateUpdate,
  onRendererDispatch,
} = {}) {
  const { userId, accessKey } = getStoredSession() ?? {};
  const { publishGoalEvent, brokerStatus } = useStompService(isLive, { userId, accessKey });

  /**
   * OPUS-1521 — held in a REF, deliberately not in the memo deps below.
   *
   * The event service asks this getter on every trigger(), so a toggle takes
   * effect on the very next event while the memo keeps its identity. Putting
   * isNleEnabled in the deps would rebuild the service on every NLE toggle and
   * hand TXLive a new `eventService`, invalidating every useCallback closed over
   * it — mid-match.
   */
  const nleRef = useRef(isNleEnabled);
  useEffect(() => { nleRef.current = isNleEnabled; }, [isNleEnabled]);

  // Held in a ref for the same reason: read at trigger() time, so the name
  // arriving after the service is built never rebuilds it.
  const fixtureNameRef = useRef(fixtureName);
  useEffect(() => { fixtureNameRef.current = fixtureName; }, [fixtureName]);

  /**
   * OPUS-1619 — the NLE scene definitions for this profile.
   *
   * resolveSceneDefinition has a stable identity by construction (the hook
   * holds the definitions in a ref), so it is safe in the memo deps below and
   * a fetch completing mid-match does not rebuild the service.
   */
  const { resolveSceneDefinition, noteCaptionUse, status: sceneStatus } = useSceneDefinitions(renderer);
  // Exposed so callers can forward it to components that publish directly
  // (e.g. StatsPanel clock events that bypass the plugin pipeline).

  const eventService = useMemo(() => {
    const publish = (mqItems, meta = {}) => {
      const tag = meta.tag || '[MQ.publish]';
      console.log(`${tag} forwarding ${mqItems.length} item(s) to STOMP`);
      mqItems.forEach((item, i) => publishGoalEvent(item, { ...meta, index: i }));
    };

    const ddsDrivers = {
      patch: async (patches, meta = {}) => {
        const tag = meta.tag || '[DDS.patch]';
        const session = getStoredSession();
        const originSite = session?.username || session?.user_name || 'Op1';
        const addedBy = session?.username || 'Op1';
        for (let i = 0; i < patches.length; i++) {
          const p = patches[i];
          const patchId = `P${Date.now()}_${Math.random().toString(36).substr(2, 9)}_${p.patchIdSuffix || 'patch'}`;
          console.log(`${tag} patch[${i}] -> ${p.collection}.${p.path} = ${p.value} (patch_id=${patchId})`);
          const patchPayload = sendPatchesFromDrivePreset({
            collection: p.collection,
            originSite,
            op: p.op || 'PATCH',
            valid_from: new Date().toISOString(),
            added_by: addedBy,
            patch_id: patchId,
            path: p.path,
            value: p.value,
            extraKey: p.extraKey,
          });
          const t0 = performance.now();
          const res = await dds.http.request({ query: patchPayload.query, variables: patchPayload.variables });
          console.log(`${tag} patch[${i}] ✓ DDS responded in ${(performance.now() - t0).toFixed(1)}ms`, res);
        }
      },

      sendLiveEvent: async ({ eventId, eventData, fixtureId, competitionId, seasonId }, meta = {}) => {
        const tag = meta.tag || '[DDS.sendLiveEvent]';
        if (!eventId || !fixtureId || !competitionId || !seasonId) {
          console.warn(`${tag} skipped: missing keys`, { eventId, fixtureId, competitionId, seasonId });
          return;
        }
        if (eventData?.event_op === 'update') {
          const session = getStoredSession();
          const originSite = session?.username || session?.user_name || 'aedrive';
          console.log(`${tag} (update) -> sendLiveEvent (upsert) event_id=${eventId}`, eventData);
          // Build the mutation directly (not sendLiveEventPreset) — the preset's
          // client-side validation requires data.team_id as a string, which
          // team-less marker events (clock / additional_time) never set. That
          // validation threw here on every additional-time update, silently
          // swallowed by eventService's safeStep, so the write never reached
          // DDS: the timeline appeared to update optimistically but reverted
          // on the next subscription tick / page refresh. sendLiveEventPreset
          // emits this exact same mutation/variables shape for the `event`
          // collection, so this is behavior-preserving for team-having events.
          const t0 = performance.now();
          const res = await dds.http.request({
            query: SEND_LIVE_EVENT_MUTATION,
            variables: {
              collection: 'event',
              key: { event_id: eventId, fixture_id: fixtureId, competition_id: competitionId, season_id: seasonId },
              data: { ...eventData, originSite, updatedAt: new Date().toISOString() },
              originSite: 'DDS',
              op: 'upsert',
            },
          });
          console.log(`${tag} ✓ DDS responded in ${(performance.now() - t0).toFixed(1)}ms`, res);
          return;
        }
        console.log(`${tag} sending live event eventId=${eventId} fixtureId=${fixtureId}`, eventData);
        // Build the mutation directly (not sendLiveEventPreset) so team-less
        // marker events (clock / additional_time) persist; goals/cards include
        // team_id in eventData and write identically.
        const t0 = performance.now();
        const res = await dds.http.request({
          query: SEND_LIVE_EVENT_MUTATION,
          variables: {
            collection: 'event',
            key: { event_id: eventId, fixture_id: fixtureId, competition_id: competitionId, season_id: seasonId },
            data: eventData,
            originSite: 'DDS',
            op: 'upsert',
          },
        });
        console.log(`${tag} ✓ DDS responded in ${(performance.now() - t0).toFixed(1)}ms`, res);
      },

      upsertStat: async ({ fixtureId, competitionId, seasonId, teamId, statType, value }, meta = {}) => {
        const tag = meta.tag || '[DDS.upsertStat]';
        console.log(`${tag} teamId=${teamId} statType=${statType} value=${value}`);
        return upsertTeamStat({ fixtureId, competitionId, seasonId, teamId, statType, value });
      },

      deleteLiveEvent: async ({ event_id, fixture_id, competition_id, season_id, reason }, meta = {}) => {
        const tag = meta.tag || '[DDS.deleteLiveEvent]';
        if (!event_id || !fixture_id || !competition_id || !season_id) {
          console.warn(`${tag} skipped: missing keys`, { event_id, fixture_id, competition_id, season_id });
          return;
        }
        const payload = removeBaseDataPreset({
          collection: 'event',
          key: { event_id, fixture_id, competition_id, season_id },
          reason: reason || 'manual',
        });
        const t0 = performance.now();
        const res = await dds.http.request({ query: payload.query, variables: payload.variables });
        console.log(`${tag} ✓ DDS responded in ${(performance.now() - t0).toFixed(1)}ms`, res);
        if (res?.errors?.length) throw new Error(res.errors[0]?.message || 'deleteLiveEvent failed');
      },
    };

    const fixtureState = onFixtureStateUpdate
      ? { update: onFixtureStateUpdate }
      : undefined;

    const renderer = onRendererDispatch
      ? { dispatch: onRendererDispatch }
      : undefined;

    const rawService = createEventService(
      { publish, dds: ddsDrivers, fixtureState, renderer },
      {
        resolvePlugin,
        resolveCaptionAction,
        resolveNleEnabled: () => nleRef.current,
        resolveSceneDefinition,
      },
    );

    // Log every event-service write (goal/card/sub/clock persistence etc.)
    // for the operator-facing Command Console — trigger() is the single
    // entry point every caller uses, so this is the one place to hook.
    return {
      ...rawService,
      trigger: async (event) => {
        // Every event carries the fixture's name for the NLE workflow, unless
        // the caller already set one.
        const params = fixtureNameRef.current && event?.fixture_name == null
          ? { ...event, fixture_name: fixtureNameRef.current }
          : event;
        const logEntry = logCommand({ source: 'event-service', payload: params });
        // Session observability — fire-and-forget, never awaited/blocking,
        // and independent of whether the underlying write below succeeds
        // (this reports the action was taken, not that it definitely landed).
        // Tagged by the app/view the operator is actually using, not by the
        // fact that it went through eventService — 'event_op'/params.type
        // already say what kind of action this was.
        const session = getStoredSession();
        logClientSessionEvent(
          session?.sessionId,
          params.type,
          isCopilotView(session) ? 'copilot' : 'ae-drive-web',
          { event_op: params.event_op, fixtureId: params.fixtureId },
          params.sessionChanges
        );
        try {
          const result = await rawService.trigger(params);
          updateCommandLog(logEntry.id, { status: 'success' });
          return result;
        } catch (error) {
          updateCommandLog(logEntry.id, { status: 'error', error: error?.message });
          throw error;
        }
      },
    };
  // resolveSceneDefinition is stable by construction (useSceneDefinitions holds
  // the definitions in a ref and returns a useCallback with no deps), so listing
  // it here is honest about the closure without ever rebuilding the service.
  }, [publishGoalEvent, onFixtureStateUpdate, onRendererDispatch, resolveSceneDefinition]);

  // sceneStatus is surfaced the same way brokerStatus is — read-only, for a
  // future footer indicator. 'idle' (no event has asked yet) | 'ready' (the
  // last lookup found at least one block) | 'empty' (it found none).
  return { eventService, publishGoalEvent, brokerStatus, sceneStatus, noteCaptionUse };
}
